import { call, put, select, takeLatest } from 'redux-saga/effects';

import { MATERIAL_PROVIDERS, STOCK_STATUSES, STOCK_TYPES } from 'constants/all';

import {
  materialWidget,
  stockInterrogatingList,
  visualize,
} from 'modules/common/forms/materialWidget/data/actions';
import { getSelectedMaterialId } from 'modules/common/forms/materialWidget/data/reducer';
import {
  getActivePartFileStatus,
  getPartFilePacket,
  getPartFileUuid,
  getProvider,
} from 'modules/common/forms/materialWidget/data/reducer';
import { getViewerIdForStockResultUuid } from 'modules/common/forms/materialWidget/data/sagas';
import { MATERIAL_INDICATOR } from 'modules/common/viewer/cadViewer/constants';

import {
  materialWidget as materialWidgetService,
  stockRequest as stockRequestService,
} from 'services/materialService';

import {
  getMaterialById,
  getSupplierMaterialsByMaterialId,
} from 'store/materials/reducer';

import { formatPrice } from 'utils/formatters';
import { inchesToMm } from 'utils/helpers';

import { types, nestingRequest, sheetRequest, sheetWidget } from './actions';
import { getCachedNestingValues, getIsMounted } from './reducer';

export default function* root() {
  yield takeLatest(types.SHEET_REQUEST_SUBMIT, watchSubmitSheetRequest);
  yield takeLatest(types.NESTING_REQUEST_SUBMIT, watchSubmitNestingRequest);
  yield takeLatest(
    types.ON_UPDATE_PART_FILE_PACKET,
    watchOnUpdatePartFilePacket,
  );
}

function* watchSubmitSheetRequest(action) {
  const { payload } = action;
  const provider = yield select(getProvider);
  try {
    const response = yield call(
      stockRequestService.sheet,
      MATERIAL_PROVIDERS[provider].urlParam,
      payload,
    );
    yield put(sheetRequest.receive({ sheets: response }));
    yield put(sheetRequest.success());
  } catch (error) {
    yield put(sheetRequest.error(error));
  }
}

const isClose = (a, b, tol = 1e-4) => {
  return Math.abs(a - b) < tol;
};

const findNestingInfoForValues = (
  nestingInfo,
  {
    edgeBuffer,
    isManual,
    partBuffer,
    partLength,
    partWidth,
    sheetLength,
    sheetWidth,
  },
) => {
  return nestingInfo.find(
    (info) =>
      isManual === info.isManual &&
      isClose(edgeBuffer, info.edgeBuffer) &&
      isClose(partBuffer, info.partBuffer) &&
      isClose(partLength, info.partLength) &&
      isClose(partWidth, info.partWidth) &&
      isClose(sheetLength, info.sheetLength) &&
      isClose(sheetWidth, info.sheetWidth),
  );
};

const getResultDisplayKey = (
  {
    edgeBuffer,
    isManual,
    partBuffer,
    partLength,
    partWidth,
    sheetLength,
    sheetWidth,
  },
  quantity,
  uuid,
  bodyId,
) => {
  return `${edgeBuffer}${isManual}${partBuffer}${partLength}${partWidth}${sheetLength}${sheetWidth}${quantity}${uuid}${MATERIAL_INDICATOR}${bodyId}`;
};

const constructResult = (partFilePacket, nestingInfo, values, bodyId) => {
  const { hasGeometry, partUuid, stockInformation, uuid } = partFilePacket;
  const { sheet } = stockInformation;
  const { canUseBaseGeometry, nestingJson } = sheet;
  const {
    edgeBuffer,
    isManual,
    partBuffer,
    partLength,
    partWidth,
    partsPerSheet,
    sheetLength,
    sheetWidth,
    thumbnailUrl,
    transformsUrl,
  } = nestingInfo;
  const { quantity, partThickness, sheetCost, stock, isSheetSizeManual } =
    values;
  const sheetCount = Math.ceil(quantity / partsPerSheet);
  const resultDisplayKey = getResultDisplayKey(
    nestingInfo,
    quantity,
    uuid,
    bodyId,
  );
  let resultKey = resultDisplayKey;
  let totalPrice = null,
    unitPrice = null,
    stockLink = null,
    stockThickness = null;
  if (stock) {
    resultKey += stock.id.toString();
    totalPrice = formatPrice(stock.price * sheetCount);
    unitPrice =
      (stock.price * sheetCount * 100) % quantity === 0
        ? formatPrice((stock.price * sheetCount) / quantity)
        : formatPrice((stock.price * sheetCount) / quantity, 3);
    stockLink = stock.link;
    stockThickness = inchesToMm(stock.thickness);
  } else {
    if (sheetCost) {
      totalPrice = formatPrice(sheetCost * sheetCount);
      unitPrice =
        (sheetCost * sheetCount * 100) % quantity === 0
          ? formatPrice((sheetCost * sheetCount) / quantity)
          : formatPrice((sheetCost * sheetCount) / quantity, 3);
    }
    stockThickness = partThickness;
  }

  const canVisualize = isManual
    ? Boolean(hasGeometry && transformsUrl)
    : Boolean(hasGeometry && transformsUrl && nestingJson);

  return {
    bodyId,
    canVisualize,
    edgeBuffer,
    hasStock: Boolean(stock),
    isManual,
    isSheetSizeManual,
    key: resultDisplayKey, // use for reference in visualize sagas
    nestingJson,
    partBuffer,
    partLength,
    partWidth,
    partsPerSheet,
    partUuid,
    quantity,
    resultKey,
    sheetCount,
    sheetCost: sheetCost ? formatPrice(sheetCost) : null,
    sheetLength,
    sheetWidth,
    stockLink,
    stockThickness,
    stockType: STOCK_TYPES.SHEET,
    thumbnailUrl,
    totalPrice,
    transformsUrl,
    unitPrice,
    useBaseGeometry: canUseBaseGeometry && !isManual,
    uuid,
  };
};

function* watchSubmitNestingRequest(action) {
  const { payload } = action;

  const materialId = yield select(getSelectedMaterialId);
  const supplierMaterials = yield select(
    getSupplierMaterialsByMaterialId,
    materialId,
  );
  let materialName;
  if (supplierMaterials && supplierMaterials.length === 1) {
    materialName = supplierMaterials[0].displayName;
  } else {
    const material = yield select(getMaterialById, materialId);
    materialName = (material || {}).name;
  }

  const partFilePacket = yield select(getPartFilePacket);
  const { nestingInfo } = partFilePacket;
  const applicableInfo = findNestingInfoForValues(nestingInfo, payload);
  if (applicableInfo) {
    const bodyId = yield call(
      getViewerIdForStockResultUuid,
      partFilePacket.uuid,
    );
    const result = constructResult(
      partFilePacket,
      applicableInfo,
      payload,
      bodyId,
    );
    result.material = materialName;

    yield put(sheetWidget.receiveNestingResult({ nestingResult: result }));
    if (result.canVisualize) {
      yield put(visualize.showRequest({ result }));
    } else {
      yield put(materialWidget.turnOffVisualizationIfNecessary());
    }
  } else {
    // dispatch a nesting request to the backend and update surrounding statuses
    try {
      const activeUuid = yield select(getPartFileUuid);
      yield call(materialWidgetService.nestingRequest, activeUuid, payload);

      const existingStatus = yield select(getActivePartFileStatus);
      const fakeStatus = {
        [activeUuid]: {
          ...existingStatus,
          [STOCK_TYPES.NESTING]: STOCK_STATUSES.INTERROGATING,
        },
      };
      yield put(
        materialWidget.receiveStatuses({ partFileStatuses: fakeStatus }),
      );
      yield put(stockInterrogatingList.add({ uuid: activeUuid }));
    } catch (error) {
      yield put(nestingRequest.error(error));
    }
  }

  yield put(
    nestingRequest.receiveCachedNestingValues({
      ...payload,
      material: materialName,
    }),
  );
  yield put(nestingRequest.success());
}

function* watchOnUpdatePartFilePacket(action) {
  const { payload } = action;
  const { partFilePacket } = payload;
  const cachedValues = yield select(getCachedNestingValues);
  const isMounted = yield select(getIsMounted);
  if (cachedValues && isMounted) {
    const { nestingInfo } = partFilePacket;
    const applicableInfo = findNestingInfoForValues(nestingInfo, cachedValues);
    if (applicableInfo) {
      const bodyId = yield call(
        getViewerIdForStockResultUuid,
        partFilePacket.uuid,
      );
      const result = constructResult(
        partFilePacket,
        applicableInfo,
        cachedValues,
        bodyId,
      );
      result.material = cachedValues.material;
      if (result.canVisualize) {
        yield put(visualize.showRequest({ result }));
      } else {
        yield put(materialWidget.turnOffVisualizationIfNecessary());
      }
      yield put(sheetWidget.receiveNestingResult({ nestingResult: result }));
    }
  }
}
